<script setup>
import { inject, ref } from 'vue';
import { useDashboardStore } from '@/stores/dashboard';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { useToast } from 'vue-toastification';

const toast = useToast();
const isLoading = inject('isLoading');
const { submit } = useDashboardStore();

const window = ref(0);
const blankForm = {
    step_1: {
        industry: null,
        industry_other: null,
        secondary_industry: null,
        secondary_industry_other: null,
    },
    step_2: {
        purpose: null,
        purpose_other: null,
        secondary_purpose: null,
        secondary_purpose_other: null,
    },
    step_3: {
        project_name: '',
        description: '',
    },
    step_4: {
        pages: null,
        user_roles: null,
        customization_needs: '',
        integration_requirements: '',
        mobile_app: false,
    },
    step_5: {
        iot: false,
        ai_ml: false,
        data_migration: false,
        data_migration_description: '',
    },
    step_6: {
        payment_processing: null,
        payment_processor: [],
        payment_processor_other: '',
    },
    step_7: {
        dashboard: null,
    },
    step_8: {
        additional_notes: '',
    },
    step_9: {
        name: '',
        email: '',
        phone: '',
        currency: 'USD',
    },
};

const form = ref({
    ...blankForm,
});

// Testing data
//form.value = JSON.parse('{"step_7":{"name":"Bennett","email":"ben.hojka@fabledsolutions.com","phone":"7802979357","currency":"CAD"},"step_1":{"project_name":"Pet Date","industry":"Software & IT Services","industry_other":"","secondary_industry":"Other","secondary_industry_other":"Dating","purpose":"Appointment Scheduling","purpose_other":"","secondary_purpose":"E-commerce","secondary_purpose_other":"","description":"A dating app for pet owners to connect and schedule dates"},"step_2":{"pages":"1-5","user_roles":"1-2","customization_needs":"","integration_requirements":"","mobile_app":false},"step_3":{"iot":false,"ai_ml":false,"data_migration":false,"data_migration_description":""},"step_4":{"payment_processing":true,"payment_processor":["Stripe","Other"],"payment_processor_other":"Moneris"},"step_5":{"dashboard":"Basic (Minimal analytics, few simple charts)"},"step_6":{"additional_notes":""},"dashboard":"Basic (Minimal analytics, few simple charts)","step":5}');

const industryOptions = ref([
    'Agriculture',
    'E-commerce',
    'Education',
    'Finance',
    'Healthcare',
    'Hospitality',
    'Logistics & Transportation',
    'Manufacturing',
    'Real Estate',
    'Retail',
    'Software & IT Services',
    'Other',
]);

const purposeOptions = ref([
    'Inventory Management',
    'E-commerce',
    'Logistics & Supply Chain',
    'CRM',
    'Business Intelligence & Reporting',
    'Internal Operations',
    'Appointment Scheduling',
    'Community Portal',
    'Other',
]);

const screenOptions = ref([
    '1-5',
    '6-10',
    '11-20',
    '21+',
]);

const userRoles = ref([
    '1-2',
    '3-4',
    '4+',
]);

const dashboardOptions = ref([
    'None (No dashboard required)',
    'Basic (Minimal analytics, few simple charts)',
    'Moderate (Several graphs/charts, but no advanced analytics)',
    'Advanced (Heavy use of data visualization, real-time analytics, complex dashboards)',
]);

const quote = ref('');
const errors = ref({});

const save = (step) => {
    submit({
        ...(step === Object.keys(form.value).length ? {
            ...form.value,
            ...form.value[`step_${step}`],
        } : form.value[`step_${step}`]),
        step: step,
        steps: Object.keys(form.value).length,
    }).then(response => {
        if (response.data.quote) {
            quote.value = response.data.quote;
        }
        window.value++;
    }).catch(error => {
        errors.value = error.response?.data?.errors;
        toast.error('Please correct the errors before proceeding');
    });
};

const getError = (key) => {
    return errors.value[key] ? errors.value[key][0] : null;
};
</script>

<template>
<div class="quote-container lg:h-screen relative">
    <div class="bg-white z-50 w-[95vw] lg:w-[80vw] py-4 lg:py-12 rounded-lg overflow-scroll my-4 lg:my-12">
        <v-icon
            v-if="window >= 1 && window <= Object.keys(form).length - 1"
            icon="mdi-arrow-left"
            color="secondary"
            class="cursor-pointer hover:bg-grey-300 p-4 rounded-full ml-2 lg:ml-6 mb-6"
            @click="window--" />
        <v-window v-model="window">
            <v-window-item>
                <v-card class="px-4 lg:px-20">
                    <img src="/images/logo.png" class="my-12 lg:my-24 w-64 mx-auto">
                    <h1 class="text-center mb-3 font-thin tracking-tighter">Get a quote for your app in minutes</h1>
                    <p class="text-center mb-24 lg:mb-48">Retrieve a quick price estimate with our simple calculator app with just a few selections.</p>
                </v-card>
            </v-window-item>

            <v-window-item>
                <v-card class="px-4 lg:px-20">
                    <div class="lg:grid flex flex-col lg:grid-cols-2 gap-6 mt-2">
                        <div class="flex flex-col gap-2 col-span-2">
                            <v-label :class="{'text-red-700': getError('industry') && !form.step_1.industry}">{{ getError('industry') && !form.step_1.industry ? getError('industry') : 'Industry' }}</v-label>
                            <div class="flex gap-4 flex-wrap">
                                <div
                                    v-for="option in industryOptions"
                                    :key="option"
                                    class="flex items-center rounded-lg px-6 cursor-pointer py-2 border border-grey-400 hover:bg-secondary hover:text-white transition-all duration-150"
                                    :class="form.step_1.industry === option ? 'bg-secondary text-white' : 'bg-grey-300 text-grey-700'"
                                    @click="form.step_1.industry = option">
                                    {{ option }}
                                </div>
                                <div class="w-64">
                                    <StyledInput
                                        v-if="form.step_1.industry === 'Other'"
                                        v-model="form.step_1.industry_other"
                                        name="industry_other"
                                        hint="Primary industry other"
                                        class="mt-0.5"
                                        hide-details
                                        persistent-hint
                                        label="Other Industry" />
                                </div>
                            </div>
                            <div class="text-grey-600 text-[12px]">Primary Industry</div>
                        </div>

                        <div class="flex flex-col gap-2 col-span-2">
                            <v-label :class="{'text-red-700': getError('secondary_industry') && !form.step_1.secondary_industry}">{{ getError('secondary_industry') && !form.step_1.secondary_industry ? getError('secondary_industry') : 'Secondary Industry' }}</v-label>
                            <div class="flex gap-4 flex-wrap">
                                <div
                                    v-for="option in industryOptions"
                                    :key="option"
                                    class="flex items-center rounded-lg px-6 cursor-pointer py-2 border border-grey-400 hover:bg-secondary hover:text-white transition-all duration-150"
                                    :class="form.step_1.secondary_industry === option ? 'bg-secondary text-white' : 'bg-grey-300 text-grey-700'"
                                    @click="form.step_1.secondary_industry = option">
                                    {{ option }}
                                </div>
                                <div class="w-64">
                                    <StyledInput
                                        v-if="form.step_1.secondary_industry === 'Other'"
                                        v-model="form.step_1.secondary_industry_other"
                                        name="secondary_industry_other"
                                        hint="Secondary industry other"
                                        class="mt-0.5"
                                        hide-details
                                        persistent-hint
                                        label="Other Industry" />
                                </div>
                            </div>
                            <div class="text-grey-600 text-[12px]">Secondary Industry (if applicable)</div>
                        </div>
                    </div>
                </v-card>
            </v-window-item>

            <v-window-item>
                <v-card class="px-4 lg:px-20">
                    <div class="lg:grid flex flex-col lg:grid-cols-2 gap-6 mt-2">
                        <div class="flex flex-col gap-2 col-span-2">
                            <v-label :class="{'text-red-700': getError('purpose') && !form.step_2.purpose}">{{ getError('purpose') && !form.step_2.purpose ? getError('purpose') : 'Purpose' }}</v-label>
                            <div class="flex gap-4 flex-wrap">
                                <div
                                    v-for="option in purposeOptions"
                                    :key="option"
                                    class="flex items-center rounded-lg px-6 cursor-pointer py-2 border border-grey-400 hover:bg-secondary hover:text-white transition-all duration-150"
                                    :class="form.step_2.purpose === option ? 'bg-secondary text-white' : 'bg-grey-300 text-grey-700'"
                                    @click="form.step_2.purpose = option">
                                    {{ option }}
                                </div>
                                <div class="w-64">
                                    <StyledInput
                                        v-if="form.step_2.purpose === 'Other'"
                                        v-model="form.step_2.purpose_other"
                                        name="purpose_other"
                                        class="mt-0.5"
                                        hide-details
                                        persistent-hint
                                        label="Other Purpose" />
                                </div>
                            </div>
                            <div class="text-grey-600 text-[12px]">Other Purpose</div>
                        </div>

                        <div class="flex flex-col gap-2 col-span-2">
                            <v-label :class="{'text-red-700': getError('secondary_purpose') && !form.step_2.secondary_purpose}">{{ getError('secondary_purpose') && !form.step_2.secondary_purpose ? getError('secondary_purpose') : 'Secondary Purpose' }}</v-label>
                            <div class="flex gap-4 flex-wrap">
                                <div
                                    v-for="option in purposeOptions"
                                    :key="option"
                                    class="flex items-center rounded-lg px-6 cursor-pointer py-2 border border-grey-400 hover:bg-secondary hover:text-white transition-all duration-150"
                                    :class="form.step_2.secondary_purpose === option ? 'bg-secondary text-white' : 'bg-grey-300 text-grey-700'"
                                    @click="form.step_2.secondary_purpose = option">
                                    {{ option }}
                                </div>
                                <div class="w-64">
                                    <StyledInput
                                        v-if="form.step_2.secondary_purpose === 'Other'"
                                        v-model="form.step_2.secondary_purpose_other"
                                        name="secondary_purpose_other"
                                        class="mt-0.5"
                                        hide-details
                                        persistent-hint
                                        label="Other Purpose" />
                                </div>
                            </div>
                            <div class="text-grey-600 text-[12px]">Secondary Purpose (if applicable)</div>
                        </div>

                    </div>
                </v-card>
            </v-window-item>
            <v-window-item>
                <v-card class="px-4 lg:px-20">
                    <div class="lg:grid flex flex-col lg:grid-cols-2 gap-6 mt-2">
                        <StyledInput
                            v-model="form.step_3.project_name"
                            class="col-span-2"
                            name="project_name"
                            hint="Name of the project"
                            persistent-hint
                            label="Project Name" />
                        <StyledTextArea
                            v-model="form.step_3.description"
                            class="col-span-2"
                            persistent-hint
                            name="description"
                            hint="Short explanation of the project"
                            label="Brief Description" />
                    </div>
                </v-card>
            </v-window-item>
            <v-window-item>
                <v-card class="px-4 lg:px-20">
                    <div class="lg:grid flex flex-col lg:grid-cols-2 gap-6">
                        <div class="flex flex-col gap-2">
                            <v-label :class="{'text-red-700': getError('pages') && !form.step_4.pages}">{{ getError('pages') && !form.step_4.pages ? getError('pages') : 'Number of Unique Pages/Screens' }}</v-label>
                            <div class="flex gap-4 flex-wrap">
                                <div
                                    v-for="option in screenOptions"
                                    :key="option"
                                    class="flex items-center rounded-lg px-6 cursor-pointer py-2 border border-grey-400 hover:bg-secondary hover:text-white transition-all duration-150"
                                    :class="form.step_4.pages === option ? 'bg-secondary text-white' : 'bg-grey-300 text-grey-700'"
                                    @click="form.step_4.pages = option">
                                    {{ option }}
                                </div>
                            </div>
                            <div class="text-grey-600 text-[12px]">Approximate number of UI screens</div>
                        </div>

                        <div class="flex flex-col gap-2">
                            <v-label :class="{'text-red-700': getError('user_roles') && !form.step_4.user_roles }">{{ getError('user_roles') && !form.step_4.user_roles ? getError('user_roles') : 'Number of User Roles' }}</v-label>
                            <div class="flex gap-4 flex-wrap">
                                <div
                                    v-for="option in userRoles"
                                    :key="option"
                                    class="flex items-center rounded-lg px-6 cursor-pointer py-2 border border-grey-400 hover:bg-secondary hover:text-white transition-all duration-150"
                                    :class="form.step_4.user_roles === option ? 'bg-secondary text-white' : 'bg-grey-300 text-grey-700'"
                                    @click="form.step_4.user_roles = option">
                                    {{ option }}
                                </div>
                            </div>
                            <div class="text-grey-600 text-[12px]">Different types of users with varying permissions</div>
                        </div>

                        <StyledTextArea
                            v-model="form.step_4.customization_needs"
                            name="customization_needs"
                            hint="Describe specific customization required"
                            persistent-hint
                            class="col-span-2"
                            label="Customization Needs" />
                        <StyledTextArea
                            v-model="form.step_4.integration_requirements"
                            name="integration_requirements"
                            hint="Describe any required integrations"
                            class="col-span-2"
                            persistent-hint
                            label="Integration Requirements" />
                        <StyledRoundSwitch
                            v-model="form.step_4.mobile_app"
                            name="mobile_app"
                            color="primary"
                            hint="Does this project need both a mobile app and a web app?"
                            persistent-hint
                            label="Mobile & Web App Requirements" />
                    </div>
                </v-card>
            </v-window-item>

            <v-window-item>
                <v-card class="px-4 lg:px-20 lg:grid flex flex-col grid-cols-3 gap-6 mt-2 py-24">
                    <StyledRoundSwitch
                        v-model="form.step_5.iot"
                        name="iot"
                        hint="Will this utilize IoT?"
                        persistent-hint
                        label="IoT" />
                    <StyledRoundSwitch
                        v-model="form.step_5.ai_ml"
                        name="ai_ml"
                        hint="Will this leverage AI or ML?"
                        persistent-hint
                        label="AI/ML" />
                    <StyledRoundSwitch
                        v-model="form.step_5.data_migration"
                        name="data_migration"
                        hint="Will this require data migration?"
                        persistent-hint
                        label="Data Migration" />
                    <StyledTextArea
                        v-if="form.step_5.data_migration"
                        v-model="form.step_5.data_migration_description"
                        name="data_migration_description"
                        class="col-span-full"
                        hint="Additional details on the source and format"
                        persistent-hint
                        label="Data Source & Format" />
                </v-card>
            </v-window-item>

            <v-window-item>
                <v-card class="px-4 lg:px-20 lg:grid flex flex-col gap-6">
                    <StyledRoundSwitch
                        v-model="form.step_6.payment_processing"
                        name="payment_processing"
                        hint="Will users be able to make transactions?"
                        persistent-hint
                        label="Will this project require payment processing?" />
                    <StyledDropdown
                        v-model="form.step_6.payment_processor"
                        :disabled="!form.step_6.payment_processing"
                        :options="['Stripe', 'PayPal', 'Square', 'Authorize.Net', 'Other']"
                        name="payment_processor"
                        hint="Select all that apply"
                        persistent-hint
                        multiple
                        label="Payment Processor" />
                    <StyledInput
                        v-model="form.step_6.payment_processor_other"
                        :disabled="!form.step_6.payment_processor.some(x => x === 'Other')"
                        name="payment_processor_other"
                        persistent-hint
                        hint="Other Payment Processor"
                        label="Other" />
                </v-card>
            </v-window-item>

            <v-window-item>
                <v-card class="px-4 lg:px-20 lg:grid flex flex-col gap-6 pt-4">
                    <div class="flex flex-col gap-2">
                        <v-label :class="{'text-red-700': getError('dashboard') && !form.step_7.dashboard}">{{ getError('dashboard') && !form.step_7.dashboard ? getError('dashboard') : 'Dashboard & Data Visualization Level' }}</v-label>
                        <div class="grid grid-cols-1 lg:grid-cols-2 gap-4">
                            <div
                                v-for="option in dashboardOptions"
                                :key="option"
                                class="w-full flex items-center text-center justify-center h-20 xl:h-16 rounded-lg px-6 cursor-pointer py-2 border border-grey-400 hover:bg-secondary hover:text-white transition-all duration-150"
                                :class="form.step_7.dashboard === option ? 'bg-secondary text-white' : 'bg-grey-300 text-grey-700'"
                                @click="form.step_7.dashboard = option">
                                {{ option }}
                            </div>
                        </div>
                        <div class="text-grey-600 text-[12px]">How much reporting/analytics is needed?</div>
                    </div>
                </v-card>
            </v-window-item>

            <v-window-item>
                <v-card class="px-4 lg:px-20 lg:grid flex flex-col gap-6 pt-4">
                    <StyledTextArea
                        v-model="form.step_8.additional_notes"
                        :options="dashboardOptions"
                        name="dashboard"
                        hint="Any extra information you want to provide"
                        persistent-hint
                        label="Additional Notes" />
                </v-card>
            </v-window-item>

            <v-window-item>
                <v-card class="px-4 lg:px-20 lg:grid grid-cols-2 flex flex-col gap-2 pt-4">
                    <StyledInput
                        v-model="form.step_9.name"
                        name="name"
                        hint="Name"
                        label="Name (optional)" />
                    <StyledInput
                        v-model="form.step_9.email"
                        type="email"
                        name="email"
                        hint="Email"
                        label="Email (optional)" />
                    <StyledMask
                        v-model="form.step_9.phone"
                        mask="###-###-####"
                        name="phone"
                        hint="Phone"
                        label="Phone (optional)" />
                    <StyledDropdown
                        v-model="form.step_9.currency"
                        :options="['USD', 'CAD', 'EUR', 'GBP']"
                        name="currency"
                        hint="Currency"
                        label="Currency (optional)" />
                    <p class="text-center text-grey-700 italic col-span-2">Lastly, add your contact information so we can follow up with your quote.</p>
                </v-card>
            </v-window-item>

            <v-window-item>
                <v-card class="px-4 lg:px-20 flex flex-col gap-6 justify-center items-center pt-4">
                    <h5 class="text-center">{{ quote }}</h5>

                    <StyledButton
                        color="secondary"
                        class="px-12"
                        @click="window = 0">
                        Start Over <FontAwesomeIcon class="ml-2" icon="redo" />
                    </StyledButton>
                </v-card>
            </v-window-item>
        </v-window>

        <div class="flex justify-center my-6">
            <v-btn
                v-if="window < Object.keys(form).length + 1"
                color="secondary"
                :disabled="isLoading"
                class="px-12 flex gap-2"
                size="x-large"
                text
                @click="save(window)">
                <template v-if="window === 0">
                    Get Started
                </template>
                <template v-else-if="window < Object.keys(form).length">
                    Next
                </template>
                <template v-else>
                    Get Quote
                </template>
                <FontAwesomeIcon v-if="isLoading" class="fa-spin" icon="sync" />
            </v-btn>
        </div>
    </div>
</div>
</template>

